/**
 * Double-buffer queue to safely batch events without allocating new arrays.
 * - Producers push into `queue`.
 * - Consumer swaps `queue` <-> `drain`, processes `drain`, then clears `drain`.
 *
 * This prevents reentrancy issues: items added during drain go into the new `queue`.
 */
export class DoubleBufferQueue<T> {
  private queue: T[] = [];
  private drain: T[] = [];

  constructor(private readonly name?: string) {}

  /** Number of items currently queued (not yet swapped into drain). */
  queued(): number {
    return this.queue.length;
  }

  /** Number of items currently in the drain buffer (usually 0 outside draining). */
  draining(): number {
    return this.drain.length;
  }

  /** Add one item to the active queue. */
  enqueue(item: T): void {
    this.queue.push(item);
  }

  /** Add multiple items efficiently. */
  enqueueAll(items: ArrayLike<T>): void {
    // Avoid spread for very large arrays; push in a loop
    for (let i = 0; i < items.length; i++) this.queue.push(items[i]);
  }

  /**
   * Swap queue <-> drain and return the drain batch.
   * The returned array is owned by this instance; do not retain it.
   */
  swap(): T[] {
    const tmp = this.drain;
    this.drain = this.queue;
    this.queue = tmp;
    return this.drain;
  }

  /**
   * Swap and drain the batch through `fn`.
   * Items enqueued during `fn` go into the fresh `queue` and will be processed next tick.
   */
  drainTo(fn: (item: T) => void): void {
    this.swap();
    for (let i = 0; i < this.drain.length; i++) {
      fn(this.drain[i]);
    }
    this.drain.length = 0; // explicit clear, no unlinking
  }

  /** Clear both buffers (e.g., on shutdown). */
  clearAll(): void {
    this.queue.length = 0;
    this.drain.length = 0;
  }
}
